/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { getLanguageMatch, getUserLanguageCode, languageCodeToName } from 'js/lib/language';
import { getLabelForAutotranslation } from 'js/lib/languageLocalized';
import { isAuthenticatedUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';
import { TranslationIcon } from '@coursera/cds-icons';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { Button } from '@coursera/event-pulse/react';

import ErrorBoundaryWithLogging from 'bundles/page/components/ErrorBoundaryWithLogging';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useLanguageInfoDialog } from 'bundles/unified-description-page-common/contexts/LanguageInfoDialogContext';
import type { Languages } from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';
import getLanguagesMetadata from 'bundles/unified-description-page-common/utils/getLanguagesMetadata';

import _t from 'i18n!nls/xdp';

const styles = {
  container: css`
    margin: var(--cds-spacing-150) 0;
    flex-wrap: wrap;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  link: css`
    padding: 0;
    color: var(--cds-color-neutral-primary);
    font-weight: normal;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);
      background: transparent;
    }
  `,
  divider: css`
    border-left: 1px solid var(--cds-color-neutral-primary-weak);
    height: 1em;
  `,
};

type Props = {
  languages?: Languages;
};

const HeroLanguageInfo = ({ languages }: Props) => {
  const { openDialog } = useLanguageInfoDialog();
  const eventingProductData = useEventingProductData();

  if (!languages) return null;

  const { autoTranslations, humanTranslations, subtitles, originalLanguage, dubbedLanguages } =
    getLanguagesMetadata(languages);

  const userLanguageForAiDubbing = getAudienceLanguageCode(languages.dubbedLanguages);
  const enableAiDubbingOnXdp = userLanguageForAiDubbing && dubbedLanguages.length > 0;

  const mainLanguageDescription = enableAiDubbingOnXdp ? (
    <Typography2 component="p" variant="bodyPrimary">
      {_t('Taught in #{language} (AI Dubbing)', {
        language: languageCodeToName(userLanguageForAiDubbing),
      })}
    </Typography2>
  ) : (
    <Typography2 component="p" variant="bodyPrimary">
      {_t('Taught in #{language}', {
        language: languageCodeToName(originalLanguage),
      })}
    </Typography2>
  );
  let languageInfoLink: React.ReactNode = null;
  let disclaimerText: React.ReactNode = null;

  // combine "available languages" and "video subtitles" into one section when XDP has AI dubbing languages
  const totalTranslations =
    dubbedLanguages.length > 0
      ? Array.from(new Set([...autoTranslations, ...humanTranslations, ...subtitles, ...dubbedLanguages])).length
      : autoTranslations.length + humanTranslations.length;

  const trackingData = {
    button: { name: 'description_page_available_languages' as ButtonName },
    pageSection: {
      sectionName: 'hero_banner' as SectionName,
    },
    ...eventingProductData,
  };

  if (enableAiDubbingOnXdp) {
    languageInfoLink = (
      <Button variant="ghost" onClick={openDialog} css={styles.link} trackingData={trackingData} noViewTracking>
        <FormattedMessage
          message={_t('{languageCount, plural, =1 {# language available} other {# languages available}}')}
          languageCount={totalTranslations + 1}
        />
      </Button>
    );
  } else if (totalTranslations > 0) {
    disclaimerText = (
      <Typography2 component="p" variant="bodyPrimary" color="supportText">
        {_t('Some content may not be translated')}
      </Typography2>
    );

    const userLanguage = isAuthenticatedUser() ? getUserLanguageCode() : _t.getLocale();
    const userLanguageAutoMatch =
      !!userLanguage && autoTranslations.find((language) => getLanguageMatch(userLanguage, language));
    const userLanguageHumanMatch =
      !!userLanguage && humanTranslations.find((language) => getLanguageMatch(userLanguage, language));

    let linkMessage: string;
    let matchedLanguageName = '';
    if (userLanguageAutoMatch || userLanguageHumanMatch) {
      linkMessage = _t(
        '{languageCount, plural, =1 {# language available} other {# languages available}}, including {languageName}'
      );
      if (userLanguageHumanMatch) {
        matchedLanguageName = languageCodeToName(userLanguageHumanMatch);
      } else if (userLanguageAutoMatch) {
        matchedLanguageName = `${languageCodeToName(userLanguageAutoMatch)} (${getLabelForAutotranslation(
          userLanguageAutoMatch
        )})`;
      }
    } else {
      linkMessage = _t('{languageCount, plural, =1 {# language available} other {# languages available}}');
    }

    languageInfoLink = (
      <Button variant="ghost" onClick={openDialog} css={styles.link} trackingData={trackingData} noViewTracking>
        <FormattedMessage
          message={linkMessage}
          languageCount={totalTranslations + 1}
          languageName={matchedLanguageName}
        />
      </Button>
    );
  } else if (subtitles.length > 0) {
    languageInfoLink = (
      <Button variant="ghost" onClick={openDialog} css={styles.link} trackingData={trackingData} noViewTracking>
        {_t('Video subtitles available')}
      </Button>
    );
  }

  return (
    <ErrorBoundaryWithLogging>
      {enableAiDubbingOnXdp ? (
        <div css={styles.container}>
          {mainLanguageDescription}
          <div aria-hidden>•</div>
          {languageInfoLink}
        </div>
      ) : (
        <div css={styles.container}>
          <TranslationIcon />
          {mainLanguageDescription}
          {languageInfoLink && <div css={styles.divider} />}
          {languageInfoLink}
          {disclaimerText && <div css={styles.divider} />}
          {disclaimerText}
        </div>
      )}
    </ErrorBoundaryWithLogging>
  );
};

export default HeroLanguageInfo;
